import { loadClerkJsScript, loadClerkUIScript } from '@clerk/shared/loadClerkJsScript';
import { writable, derived } from 'svelte/store';
import { clerkAppearance, clerkScriptVersions, signInModalAppearance } from './appearance.js';

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const jwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE || 'exchange-web-api';

// Clerk v6: the npm `new Clerk()` build does NOT bundle the prebuilt UI
// components (openSignIn, etc.). We load the hosted clerk.browser.js script
// (which includes the UI) via Clerk's official loader; it attaches a fully
// constructed instance to window.Clerk. We resolve `clerk` lazily after the
// script loads — see resolveClerk()/ensureClerk() below.
let clerk = null;

const internalUser = writable(null);
export const user = derived(internalUser, ($user) => $user);
export const isAuthenticated = derived(internalUser, ($user) => !!$user);

let loadPromise = null;
let unsubscribe = null;

// Tear down the sign-in modal and the global CSS override that login() injects.
// If this is left in place after sign-in, Clerk's leftover modal backdrop sits
// at a high z-index over the app and swallows all mouse clicks.
function closeSignInUi() {
  try {
    clerk?.closeSignIn?.();
  } catch {
    /* ignore — modal may already be closed */
  }
  document.getElementById('clerk-modal-override')?.remove();

  // Under Svelte 5 event delegation, clicks only fire if they bubble to the
  // root listener. Any leftover Clerk modal backdrop/portal sitting over #app
  // swallows the click before it bubbles, killing ALL clicks. closeSignIn()
  // does not always remove these synchronously, so strip any stragglers.
  document
    .querySelectorAll('.cl-modalBackdrop, .cl-modal, [class*="cl-modal"]')
    .forEach((el) => el.remove());
}

function sync(state) {
  if (!clerk) {
    internalUser.set(null);
    return;
  }

  const currentState = state ?? clerk.getState?.();
  const session = currentState?.session ?? clerk.session;
  const currentUser = currentState?.user ?? clerk.user;

  if (session && currentUser) {
    // Authenticated: make sure no sign-in modal / override style is left
    // covering the dashboard and intercepting clicks.
    closeSignInUi();

    const email =
      currentUser.primaryEmailAddress?.emailAddress ??
      currentUser.emailAddresses?.[0]?.emailAddress ??
      '';

    // Get profile image from external account (Google) avatarUrl
    let imageUrl = null;

    // Check if user has external accounts (like Google OAuth)
    if (currentUser.externalAccounts && currentUser.externalAccounts.length > 0) {
      // Find Google account and use its avatarUrl (which contains the actual Google profile picture)
      const googleAccount = currentUser.externalAccounts.find(
        account => account.provider === 'google'
      );

      if (googleAccount && googleAccount.avatarUrl) {
        imageUrl = googleAccount.avatarUrl;
      }
    }

    // Fallback to Clerk's imageUrl if no external account avatar
    if (!imageUrl) {
      imageUrl = currentUser.imageUrl || null;
    }

    internalUser.set({
      id: currentUser.id,
      email,
      name: currentUser.fullName || currentUser.username || email,
      imageUrl: imageUrl
    });
  } else {
    internalUser.set(null);
  }
}

function ensureClerk() {
  if (!publishableKey) {
    throw new Error('Clerk publishable key is not configured.');
  }

  if (!loadPromise) {
    // Clerk v6 splits core + UI into separate scripts. The core build
    // (clerk.browser.js) provides window.Clerk; the UI build provides the
    // prebuilt components (openSignIn, etc.). Both must load before .load(),
    // otherwise openSignIn throws "Clerk was not loaded with Ui components".
    // Load the UI bundle first: it registers window.__internal_ClerkUICtor,
    // which we must hand to clerk.load() so the prebuilt components attach.
    // Then load the core script, which provides window.Clerk.
    // Both are pinned to exact versions (ADR-0041); the loader's version option
    // is Clerk-internal (`__internal_`), so re-check it on any @clerk/shared bump.
    loadPromise = loadClerkUIScript({
      publishableKey,
      __internal_clerkUIVersion: clerkScriptVersions.clerkUI
    })
      .then(() =>
        loadClerkJsScript({
          publishableKey,
          __internal_clerkJSVersion: clerkScriptVersions.clerkJS
        })
      )
      .then(() => {
        // The hosted core script attaches a constructed instance to window.Clerk.
        clerk = window.Clerk;
        if (!clerk) {
          throw new Error('Clerk failed to initialize (window.Clerk is unavailable).');
        }

        const ClerkUI = window.__internal_ClerkUICtor;
        if (!ClerkUI) {
          throw new Error('Clerk UI components failed to load (__internal_ClerkUICtor missing).');
        }

        // Passing the UI constructor is what makes openSignIn() etc. work in v6.
        return clerk.load({ appearance: clerkAppearance, ui: { ClerkUI } });
      })
      .then(() => {
        sync();
        unsubscribe = clerk.addListener((state) => sync(state));
      })
      .catch((error) => {
        console.error('Failed to load Clerk', error);
        loadPromise = null; // allow a retry on the next call
        throw error;
      });
  }

  return loadPromise;
}

export async function login() {
  await ensureClerk();

  // Already signed in: Clerk runs in single-session mode and refuses to open
  // the sign-in modal (throws "cannot_render_single_session_enabled"). Don't
  // inject the modal-override CSS or call openSignIn — just sync and bail,
  // otherwise the aborted modal/leftover styles block clicks on the app.
  if (clerk.session) {
    closeSignInUi();
    sync();
    return;
  }

  // Add CSS to prevent modal dismissal and center it
  const style = document.createElement('style');
  style.id = 'clerk-modal-override';
  style.textContent = `
    .cl-modalBackdrop {
      pointer-events: none !important;
    }
    .cl-modalContent {
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) !important;
      margin: 0 !important;
      pointer-events: auto !important;
    }
    /* Clerk focuses the dialog container on open; the app's global :focus-visible
       ring would frame the whole card. Controls inside keep their own rings. */
    .cl-modalContent:focus-visible {
      outline: none !important;
    }
    .cl-modalCloseButton {
      display: none !important;
    }
    .cl-footerAction,
    .cl-footer__action,
    .cl-footer a[href*="sign-up"] {
      display: none !important;
    }
  `;

  // Remove existing style if present
  const existingStyle = document.getElementById('clerk-modal-override');
  if (existingStyle) {
    existingStyle.remove();
  }
  document.head.appendChild(style);

  await clerk.openSignIn({
    afterSignInUrl: window.location.href,
    redirectUrl: window.location.href,
    appearance: signInModalAppearance
  });

  sync();
}

export async function logout() {
  await ensureClerk();

  await clerk.signOut();
  sync();
}

export async function acquireToken() {
  await ensureClerk();

  const session = clerk.session;
  if (!session) {
    throw new Error('No active session');
  }

  let token = await session.getToken({ template: jwtTemplate });
  if (!token) {
    await new Promise((resolve) => setTimeout(resolve, 150));
    token = await session.getToken({ template: jwtTemplate });
  }

  if (!token) {
    throw new Error('Unable to retrieve access token');
  }

  return token;
}

export function teardownAuth() {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  internalUser.set(null);
}
